import { z } from "zod";
import { DatabricksAuthType } from "@/pages/datastores/databricksTypes";

// `.trim()` on every text field — pasting from a doc / wiki often picks
// up leading or trailing whitespace, and the BE's strict regex on
// server_hostname / http_path then rejects with 422. Trim at the form
// boundary so users get a clean Test Connection.
const trimmedString = () => z.string().trim();

// Base schema for common fields. Limits match the BE schema exactly
// (PR #2155 review F5): name max 64, description max 512.
const baseSchema = z.object({
  name: trimmedString().min(1, "Name is required").max(64, "Name must be 64 characters or less"),
  description: trimmedString().max(512, "Description must be 512 characters or less").optional(),
  server_hostname: trimmedString().min(1, "Server hostname is required"),
  http_path: trimmedString().min(1, "HTTP path is required"),
  // PR #2155 review F7: auth_type is nullable rather than including "" in
  // the enum, so the inferred TS type stays clean.
  auth_type: z
    .enum([DatabricksAuthType.PAT, DatabricksAuthType.OAUTH_M2M, DatabricksAuthType.AZURE_SP])
    .nullable(),
  access_token: trimmedString().optional(),
  client_id: trimmedString().optional(),
  client_secret: trimmedString().optional(),
  azure_tenant_id: trimmedString().optional(),
});

// Form schema with conditional validation based on auth_type
export const formSchema = baseSchema.superRefine((data, ctx) => {
  if (!data.auth_type) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Authentication method is required",
      path: ["auth_type"],
    });
    return;
  }

  if (data.auth_type === DatabricksAuthType.PAT) {
    if (!data.access_token || data.access_token.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Access token is required for PAT authentication",
        path: ["access_token"],
      });
    }
  } else if (data.auth_type === DatabricksAuthType.OAUTH_M2M) {
    if (!data.client_id || data.client_id.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Client ID is required for OAuth M2M authentication",
        path: ["client_id"],
      });
    }
    if (!data.client_secret || data.client_secret.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Client Secret is required for OAuth M2M authentication",
        path: ["client_secret"],
      });
    }
  } else if (data.auth_type === DatabricksAuthType.AZURE_SP) {
    if (!data.azure_tenant_id || data.azure_tenant_id.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Tenant ID is required for Azure service principal authentication",
        path: ["azure_tenant_id"],
      });
    }
    if (!data.client_id || data.client_id.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Client ID is required for Azure service principal authentication",
        path: ["client_id"],
      });
    }
    if (!data.client_secret || data.client_secret.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Client Secret is required for Azure service principal authentication",
        path: ["client_secret"],
      });
    }
  }
});

export type DatabricksFormData = z.infer<typeof baseSchema>;

// Module-scope constant: hoisted out of the component body so it isn't
// recreated on every render.
export const AUTH_INFO_CONTENT: Record<string, string> = {
  [DatabricksAuthType.PAT]:
    "To connect with PAT, you'll need a Personal Access Token from Databricks. Create one in User Settings → Developer → Access tokens.",
  [DatabricksAuthType.OAUTH_M2M]:
    "To connect with OAuth M2M, you'll need a Service Principal with a Client ID and Secret. Set this up in your Databricks account settings under Service Principals.",
  [DatabricksAuthType.AZURE_SP]:
    "To connect with an Azure service principal, you'll need a Microsoft Entra ID app registration (Tenant ID, Client ID, and Client Secret). The service principal must be added to the Databricks workspace and granted CAN USE on the SQL warehouse.",
  "": "Select an authentication method to see connection instructions.",
};
